/**
 * MyNFTs.jsx
 *
 * Shows the connected wallet's HEXABOTS, each linking to its own page on
 * OpenSea.
 *
 * Token IDs come from the contract's `tokensOfOwner()`. Artwork comes
 * from the pre-reveal media until reveal, at which point every token
 * resolves to its own image — so this deliberately reads `tokenURI` per
 * token rather than assuming they share one, and simply falls back to
 * the shared preview when the metadata can't be fetched.
 *
 * Note on OpenSea: it only lists chains it has explicitly indexed, and
 * ARC is very new. The links are built to the standard URL shape, but if
 * OpenSea has not indexed the chain yet they will 404 until it does —
 * which is a marketplace limitation, not something the site can fix.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ethers } from 'ethers';
import Panel from '../Panel';
import GlowText from '../GlowText';
import { CONTRACT_ADDRESS, CONTRACT_ABI } from '../../lib/contractConfig';
import { ARC_TESTNET, READ_RPC_URLS, OPENSEA_ASSET_BASE, OPENSEA_COLLECTION } from '../../lib/chain';

/**
 * Public IPFS gateways, tried in order.
 *
 * A single gateway is a single point of failure — ipfs.io in particular
 * rate-limits and times out under load, which would leave every holder
 * staring at blank tiles. Falling through a few costs nothing when the
 * first one works.
 */
const IPFS_GATEWAYS = [
  'https://ipfs.io/ipfs/',
  'https://cloudflare-ipfs.com/ipfs/',
  'https://dweb.link/ipfs/',
];

const ipfsCandidates = (uri) => {
  if (!uri) return [];
  if (!uri.startsWith('ipfs://')) return [uri];
  const cid = uri.slice(7);
  return IPFS_GATEWAYS.map((g) => g + cid);
};

/** Fetch the first URL that responds. */
async function fetchFirst(urls) {
  for (const url of urls) {
    try {
      const res = await fetch(url);
      if (res.ok) return res;
    } catch { /* next gateway */ }
  }
  return null;
}

export default function MyNFTs({ address, refreshKey }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);

  const load = useCallback(async () => {
    if (!address) { setTokens([]); return; }
    setLoading(true);
    try {
      const provider = new ethers.JsonRpcProvider(
        READ_RPC_URLS[0], ARC_TESTNET.chainIdDec,
        { staticNetwork: true, batchMaxCount: 1 }
      );
      const c = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, provider);
      const ids = await c.tokensOfOwner(address);
      const list = ids.map((i) => Number(i));
      setTokens(list);

      // Read the token's real metadata and use ITS image — the pre-reveal
      // artwork the holder actually owns, not the marketing clip. Before
      // reveal every token shares one URI, so a single fetch covers them
      // all; after reveal the first token's image is still correct and
      // per-token art can be wired in the same way.
      if (list.length) {
        const uri = await c.tokenURI(list[0]);
        const res = await fetchFirst(ipfsCandidates(uri));
        if (res) {
          const meta = await res.json();
          if (meta?.image) {
            const imgRes = ipfsCandidates(meta.image);
            setImage(imgRes[0] || null);
          }
        }
      }
    } catch (err) {
      console.warn('[MyNFTs] could not read tokens:', err.message);
      setTokens([]);
    } finally {
      setLoading(false);
    }
  }, [address]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!address) return null;

  return (
    <Panel title="MY HEXABOTS">
      {loading ? (
        <div className="flex flex-col items-center gap-3 py-10">
          <span className="status-dot" />
          <GlowText as="div" text="LOADING" size={13} variant="cyan" />
        </div>
      ) : tokens.length === 0 ? (
        <div className="text-center py-10 space-y-2">
          <GlowText as="div" text="NO UNITS YET" size={15} variant="violet" />
          <p className="font-mono-hud text-dim text-fluid-sm">
            Your minted HEXABOTS will appear here.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="font-mono-hud text-dim text-xs tracking-widest">OWNED</span>
              <GlowText text={String(tokens.length)} size={16} variant="cyan" mono />
            </div>
            {OPENSEA_COLLECTION && (
              <a
                href={OPENSEA_COLLECTION}
                target="_blank" rel="noopener noreferrer"
                className="btn-robotic btn-robotic-ghost no-underline"
                style={{ padding: '6px 12px', fontSize: 10 }}
              >
                VIEW COLLECTION ↗
              </a>
            )}
          </div>

          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))' }}
          >
            {tokens.map((id) => (
              <a
                key={id}
                href={`${OPENSEA_ASSET_BASE}/${CONTRACT_ADDRESS}/${id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mech-panel no-underline"
                style={{ padding: 6, display: 'block', transition: 'transform .15s, box-shadow .15s' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 0 22px rgba(0,229,255,0.45)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div
                  style={{
                    aspectRatio: '1 / 1', overflow: 'hidden',
                    background: '#05070c', border: '1px solid var(--steel)',
                  }}
                >
                  {image ? (
                    <img
                      src={image}
                      alt={`HEXABOT #${id}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    // No promo clip here — showing the marketing video in
                    // place of a token's artwork misrepresents what the
                    // holder owns. An honest placeholder is better.
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ color: 'var(--text-dim)', fontSize: 22 }}
                    >
                      ?
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between mt-1.5 px-0.5">
                  <span className="font-mono-hud" style={{ fontSize: 11, color: 'var(--cyan)' }}>#{id}</span>
                  <span className="font-mono-hud text-dim" style={{ fontSize: 9 }}>OPENSEA ↗</span>
                </div>
              </a>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}
